import { useContext } from "react";
import { AccountStatuses, BreadAccount } from "breadcommon";
import { PlaidItemsContext } from "../firebaseio/PlaidItemsContext";
import { RyeIcon } from "../rye/RyeIcon";
import { RyeButton } from "../rye/RyeButton";
import { useRyeModal, RyeModalContent } from "../rye/RyeModal";
import { buildClasses } from "../utils/buildClasses";
import { useMinuteTick } from "../utils/customHooks";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { ReauthenticateModal } from "./ReauthenticateModal";
import { ReplaceDisappearedAccountModal } from "./ReplaceDisappearedAccountModal";

dayjs.extend(relativeTime);

export function AccountSyncStatus(props: {
  account: BreadAccount;
}): JSX.Element | null {
  const plaidItems = useContext(PlaidItemsContext);
  const { openModal } = useRyeModal();
  useMinuteTick();
  const plaidItem = props.account.plaid_item_id
    ? plaidItems.get(props.account.plaid_item_id)
    : undefined;

  if (props.account.status === AccountStatuses.CLOSED) {
    if (props.account.closed_date) {
      return (
        <div
          className={buildClasses(
            "flex",
            "items-center",
            "text-on-surface-500",
            "text-sm",
          )}
        >
          <RyeIcon
            name="archive"
            size="sm"
            className="text-on-surface-400 mr-2 flex-shrink-0"
          />
          <span className="whitespace-nowrap">
            Closed {props.account.closed_date.format("MMM D, YYYY")}
          </span>
        </div>
      );
    } else {
      return (
        <div
          className={buildClasses(
            "flex",
            "items-center",
            "text-on-surface-500",
            "text-sm",
          )}
        >
          <RyeIcon
            name="archive"
            size="sm"
            className="text-on-surface-400 mr-2 flex-shrink-0"
          />
          <span className="whitespace-nowrap">Closed</span>
        </div>
      );
    }
  }

  if (!plaidItem) {
    return null;
  }

  if (props.account.disappeared_from_plaid) {
    return (
      <RyeButton
        text="Fix connection"
        icon="warning"
        variant="outlined"
        vibe="primary"
        primaryColor="red"
        size="sm"
        onClick={() => {
          openModal(<ReplaceDisappearedAccountModal account={props.account} />);
        }}
      />
    );
  }

  const hasPlaidItemLoginRequiredError =
    plaidItem.sync_errors?.account_sync_error?.error_code ===
      "ITEM_LOGIN_REQUIRED" ||
    plaidItem.sync_errors?.transaction_sync_error?.error_code ===
      "ITEM_LOGIN_REQUIRED";

  if (hasPlaidItemLoginRequiredError) {
    return (
      <RyeButton
        text="Fix sync issues"
        icon="construction"
        variant="outlined"
        vibe="primary"
        primaryColor="red"
        size="sm"
        onClick={() => {
          openModal(
            <ReauthenticateModal plaidItemId={plaidItem.plaid_item_id} />,
          );
        }}
      />
    );
  }

  const hasOtherSyncError =
    plaidItem.sync_errors?.account_sync_error ||
    plaidItem.sync_errors?.transaction_sync_error;

  if (hasOtherSyncError) {
    return (
      <RyeButton
        text="View sync issues"
        icon="construction"
        variant="outlined"
        vibe="primary"
        primaryColor="red"
        size="sm"
        onClick={() => {
          openModal(
            <RyeModalContent
              title="See sync issue details"
              content={
                <>
                  <div className="mb-4">
                    Unfortunately, your bank's APIs are not working correctly at
                    the moment. We know this is super frustrating and we hope
                    these issues resolve soon.
                  </div>
                  <pre className="text-sm overflow-auto max-w-2xl max-h-96">
                    {JSON.stringify(plaidItem.sync_errors, null, 2)}
                  </pre>
                </>
              }
              actionButtons={[
                {
                  text: "Close",
                  variant: "outlined",
                  vibe: "subdued",
                  action: () => {},
                },
              ]}
            />,
          );
        }}
      />
    );
  }

  const lastSynced =
    plaidItem.last_successful_sync_timestamps?.transaction_sync_timestamp_secs;
  if (!lastSynced) {
    return null;
  }

  return (
    <div
      className={buildClasses(
        "flex",
        "items-center",
        "text-on-surface-500",
        "text-sm",
      )}
    >
      <div
        className={buildClasses(
          "w-1.5",
          "h-1.5",
          "rounded-full",
          "bg-green",
          "mr-2",
          "flex-shrink-0",
        )}
      />
      <span className="whitespace-nowrap">
        Synced {dayjs.unix(lastSynced).fromNow()}
      </span>
    </div>
  );
}
